import React, { useState, useEffect } from 'react';
import { DatePicker, Select, Checkbox, Switch, Button } from 'antd';
import moment from 'moment';
import { CloseOutlined } from '@ant-design/icons';

const { Option } = Select;

// Helper function to get the start of the fiscal year (July 1st)
const getFiscalYearStart = (date) => {
  const currentMoment = moment(date);
  const currentYear = currentMoment.year();
  const fiscalYearStartMonth = 6; // July
  if (currentMoment.month() < fiscalYearStartMonth) {
    return moment().year(currentYear - 1).month(fiscalYearStartMonth).startOf('month');
  }
  return moment().year(currentYear).month(fiscalYearStartMonth).startOf('month');
};

// Helper function to get the end of the fiscal year (June 30th)
const getFiscalYearEnd = (date) => {
  const currentMoment = moment(date);
  const currentYear = currentMoment.year();
  const fiscalYearEndMonth = 5; // June
  if (currentMoment.month() > fiscalYearEndMonth) {
    return moment().year(currentYear + 1).month(fiscalYearEndMonth).endOf('month');
  }
  return moment().year(currentYear).month(fiscalYearEndMonth).endOf('month');
};

const PeriodDateComponent = ({ periodCurrentFilter, attributeFilters, setAttributeFilters }) => {
  // 1. Initialize state with Moment objects (Handles case where values might be strings)
  const [dateRange, setDateRange] = useState([
    attributeFilters[periodCurrentFilter]?.fromDate ? moment(attributeFilters[periodCurrentFilter].fromDate) : null,
    attributeFilters[periodCurrentFilter]?.toDate ? moment(attributeFilters[periodCurrentFilter].toDate) : null,
  ]);

  const [includeWithoutDate, setIncludeWithoutDate] = useState(
    attributeFilters[periodCurrentFilter]?.includeWithoutDate ?? true
  );

  const [applyToAllTabs, setApplyToAllTabs] = useState(false);
  const [includeWithDate, setIncludeWithDate] = useState(true);
  const [timePeriod, setTimePeriod] = useState('Manual selection');

  // 2. Sync local state if global attributeFilters change (e.g., on Clear)
  useEffect(() => {
    const filter = attributeFilters[periodCurrentFilter];
    if (filter) {
      setDateRange([
        filter.fromDate ? moment(filter.fromDate) : null,
        filter.toDate ? moment(filter.toDate) : null,
      ]);
      setIncludeWithoutDate(filter.includeWithoutDate ?? true);
    } else {
      setDateRange([null, null]);
      setIncludeWithoutDate(true);
      setTimePeriod('Manual selection');
    }
  }, [attributeFilters, periodCurrentFilter]);

  const handleClearFilters = () => {
    setAttributeFilters({});
  };

  const handleDateRangeChange = (newRange) => {
    const [start, end] = newRange;
    setDateRange([start, end]);

    setAttributeFilters((prev) => ({
      ...prev,
      [periodCurrentFilter]: {
        ...prev[periodCurrentFilter],
        fromDate: start, // moment object
        toDate: end,     // moment object
      },
    }));
  };

  const handleIncludeWithoutDateChange = (e) => {
    const checked = e.target.checked;
    setIncludeWithoutDate(checked);
    setAttributeFilters((prev) => ({
      ...prev,
      [periodCurrentFilter]: {
        ...prev[periodCurrentFilter],
        includeWithoutDate: checked,
      },
    }));
  };

  const handleTimePeriodChange = (value) => {
    setTimePeriod(value);
    if (value === 'Manual selection') return;

    let fromDate, toDate;
    const now = moment();
    const projectEndDate = moment('2028-06-30');
    const projectStartDate = moment('2023-01-01');

    switch (value) {
      case 'Year to date':
      case 'Year to date weekly':
        fromDate = getFiscalYearStart(now);
        toDate = now;
        break;
      case 'This year':
        fromDate = getFiscalYearStart(now);
        toDate = getFiscalYearEnd(now);
        break;
      case 'Next 12 months':
        fromDate = moment().add(1, 'month').startOf('month');
        toDate = moment(fromDate).add(12, 'months').endOf('month');
        break;
      case 'Since beginning of month':
        fromDate = moment().startOf('month');
        toDate = projectEndDate;
        break;
      case 'Since beginning of this year':
        fromDate = getFiscalYearStart(now);
        toDate = projectEndDate;
        break;
      case 'Last 2 weeks and next 4 weeks':
        fromDate = now.clone().subtract(2, 'weeks').startOf('day');
        toDate = now.clone().add(4, 'weeks').endOf('day');
        break;
      case 'Last 4 weeks and next 8 weeks':
        fromDate = now.clone().subtract(4, 'weeks').startOf('day');
        toDate = now.clone().add(8, 'weeks').endOf('day');
        break;
      case 'Last 1 months and next 3 months':
        fromDate = now.clone().subtract(1, 'month').startOf('day');
        toDate = now.clone().add(3, 'months').endOf('day');
        break;
      case 'Last 3 months and next 9 months':
        fromDate = now.clone().subtract(3, 'months').startOf('day');
        toDate = now.clone().add(9, 'months').endOf('day');
        break;
      case 'Last 6 months and next 12 months':
        fromDate = now.clone().subtract(6, 'months').startOf('day');
        toDate = now.clone().add(12, 'months').endOf('day');
        break;
      case 'This year and next 2 years':
        fromDate = getFiscalYearStart(now);
        toDate = getFiscalYearEnd(moment(now).add(2, 'years'));
        break;
      case 'Until current month':
        fromDate = projectStartDate;
        toDate = now.clone().endOf('month');
        break;
      case 'Until current month -2':
        fromDate = projectStartDate;
        toDate = now.clone().subtract(2, 'months').endOf('month');
        break;
      case 'FY 2023/24':
        fromDate = moment('2023-07-01');
        toDate = moment('2024-06-30');
        break;
      case 'FY 2024/25':
        fromDate = moment('2024-07-01');
        toDate = moment('2025-06-30');
        break;
      case 'FY 2025/26':
        fromDate = moment('2025-07-01');
        toDate = moment('2026-06-30');
        break;
      case 'FY 2026/27':
        fromDate = moment('2026-07-01');
        toDate = moment('2027-06-30');
        break;
      case 'FY 2027/28':
        fromDate = moment('2027-07-01');
        toDate = moment('2028-06-30');
        break;
      case 'Last completed month':
        fromDate = moment().subtract(1, 'month').startOf('month');
        toDate = moment().subtract(1, 'month').endOf('month');
        break;
      case 'This week':
        fromDate = moment().startOf('week');
        toDate = moment().endOf('week');
        break;
      default:
        fromDate = null;
        toDate = null;
    }
    handleDateRangeChange([fromDate, toDate]);
  };

  return (
    <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Apply filter to all tabs</span>
        <Switch checked={applyToAllTabs} onChange={setApplyToAllTabs} />
        {Object.keys(attributeFilters).length > 0 && (
          <Button icon={<CloseOutlined />} onClick={handleClearFilters} type="link" danger>
            Clear Filters
          </Button>
        )}
      </div>

      <Checkbox checked={includeWithDate} onChange={(e) => setIncludeWithDate(e.target.checked)}>
        Include items with a date
      </Checkbox>

      <div>
        <div style={{ marginBottom: '4px' }}>Select a time period</div>
        <Select value={timePeriod} onChange={handleTimePeriodChange} style={{ width: '100%' }}>
          <Option value="Manual selection">Manual selection</Option>
          <Option value="Year to date">Fiscal Year to date</Option>
          <Option value="This year">This Fiscal Year</Option>
          <Option value="Next 12 months">Next 12 months</Option>
          <Option value="Since beginning of month">Since beginning of month</Option>
          <Option value="Since beginning of this year">Since beginning of this Fiscal Year</Option>
          <Option value="Last 2 weeks and next 4 weeks">Last 2 weeks and next 4 weeks</Option>
          <Option value="Last 4 weeks and next 8 weeks">Last 4 weeks and next 8 weeks</Option>
          <Option value="Last 1 months and next 3 months">Last 1 months and next 3 months</Option>
          <Option value="Last 3 months and next 9 months">Last 3 months and next 9 months</Option>
          <Option value="Last 6 months and next 12 months">Last 6 months and next 12 months</Option>
          <Option value="This year and next 2 years">This Fiscal Year and next 2 Fiscal Years</Option>
          <Option value="Until current month">Until current month</Option>
          <Option value="Until current month -2">Until current month -2</Option>
          <Option value="FY 2023/24">FY 2023/24</Option>
          <Option value="FY 2024/25">FY 2024/25</Option>
          <Option value="FY 2025/26">FY 2025/26</Option>
          <Option value="FY 2026/27">FY 2026/27</Option>
          <Option value="FY 2027/28">FY 2027/28</Option>
          <Option value="Year to date weekly">Year to date weekly</Option>
          <Option value="Last completed month">Last completed month</Option>
          <Option value="This week">This week</Option>
        </Select>
      </div>

      <div>
        <div style={{ marginBottom: '4px' }}>From</div>
        <DatePicker
          // Crucial: ensure value is moment or null
          value={dateRange[0] ? moment(dateRange[0]) : null}
          onChange={(date) => handleDateRangeChange([date, dateRange[1]])}
          disabled={timePeriod !== 'Manual selection'}
          style={{ width: '100%' }}
          placeholder="<no date selected>"
        />
      </div>

      <div>
        <div style={{ marginBottom: '4px' }}>To</div>
        <DatePicker
          // Crucial: ensure value is moment or null
          value={dateRange[1] ? moment(dateRange[1]) : null}
          onChange={(date) => handleDateRangeChange([dateRange[0], date])}
          disabled={timePeriod !== 'Manual selection'}
          style={{ width: '100%' }}
          placeholder="<no date selected>"
        />
      </div>

      <Checkbox checked={includeWithoutDate} onChange={handleIncludeWithoutDateChange}>
        Include items without a date
      </Checkbox>
    </div>
  );
};

export default PeriodDateComponent;